/**
 * 2.0 wave 2, Part 3 (spec-2.0-build-wave2.md 4.4d and 7.1): the goal time contract, client half.
 *
 * An athlete may give an upcoming race an optional goal time. The wire carries
 * the text under the body field `goalTime`; the server stores whole seconds and
 * returns them as `goal_time_seconds` on the meet row. This file is the client's
 * parse and print. `api/lib/goalTime.js` (W2-SRV-GOALTIME) is the server's. Both
 * are tested against `tests/fixtures/goal-time-vectors.json`, so a vector added
 * there is run by both sides.
 *
 * Accepted (Appendix D):
 *   two-part   `m:ss` or `mm:ss`     minutes 0 to 99, seconds 00 to 59
 *   three-part `h:mm:ss` or `hh:mm:ss`  hours 0 to 23, minutes and seconds 00 to 59
 * and a total of 1 to 86399 seconds. No surrounding whitespace, no sign.
 *
 * Pure: no React, no I/O.
 */

/** The largest accepted total: 23:59:59. */
export const GOAL_TIME_MAX_SECONDS = 86399;

const TWO_PART = /^(\d{1,2}):([0-5]\d)$/;
const THREE_PART = /^(\d{1,2}):([0-5]\d):([0-5]\d)$/;

/** Whole seconds for a valid goal time, or null for anything else. Never throws. */
export function parseGoalTime(text: string): number | null {
  if (typeof text !== 'string') return null;

  let seconds: number;
  const three = THREE_PART.exec(text);
  if (three) {
    const hours = Number(three[1]);
    if (hours > 23) return null;
    seconds = hours * 3600 + Number(three[2]) * 60 + Number(three[3]);
  } else {
    const two = TWO_PART.exec(text);
    if (!two) return null;
    seconds = Number(two[1]) * 60 + Number(two[2]);
  }

  return seconds >= 1 && seconds <= GOAL_TIME_MAX_SECONDS ? seconds : null;
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * The inverse of `parseGoalTime` for a valid total: `m:ss` under one hour
 * ("18:59"), otherwise `h:mm:ss` ("1:05:30"). A value that is not a whole number
 * of seconds from 1 to 86399 (a null or missing column, say) prints as the empty
 * string, so a form field shows nothing rather than a wrong time.
 */
export function formatGoalTime(seconds: number): string {
  if (!Number.isInteger(seconds) || seconds < 1 || seconds > GOAL_TIME_MAX_SECONDS) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${m}:${pad2(s)}`;
}
